import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1], delay: i * 0.06 },
  }),
};

function teamGridClass(count) {
  if (count <= 1) return "mx-auto max-w-xs grid-cols-1";
  if (count === 2) return "mx-auto max-w-2xl grid-cols-2";
  if (count === 3) return "grid-cols-1 sm:grid-cols-3";
  if (count === 4) return "grid-cols-2 xl:grid-cols-4";
  return "grid-cols-2 md:grid-cols-3 xl:grid-cols-5";
}

function TeamMemberCard({ member, className = "" }) {
  const image = member?.image?.trim();
  if (!image && !member?.name) return null;

  return (
    <article
      className={`group relative overflow-hidden rounded-[2rem] aspect-[4/5] bg-[#fbd9e5]/40 ${className}`}
    >
      {image ? (
        <img
          src={image}
          alt={member.imageAlt?.trim() || member.name || ""}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#fbd9e5] to-[#3e2723]/15" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#3e2723]/88 via-[#3e2723]/20 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 text-[#fbd9e5]">
        {member.label ? (
          <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#fbd9e5]/70">
            {member.label}
          </span>
        ) : null}
        {member.name ? (
          <p className="mt-1.5 font-display text-xl font-bold tracking-tight sm:text-2xl">{member.name}</p>
        ) : null}
        {member.role ? <p className="mt-1 text-sm text-[#fbd9e5]/80">{member.role}</p> : null}
      </div>
    </article>
  );
}

export default function AboutTeamShowcase({ showcase, members = [] }) {
  const list = (Array.isArray(members) ? members : []).filter(
    (m) => m && (m.image?.trim() || m.name?.trim())
  );
  const [api, setApi] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const onSelect = useCallback((embla) => {
    setActiveIndex(embla.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!api) return undefined;
    onSelect(api);
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api, onSelect]);

  useEffect(() => {
    if (!api || list.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      if (api.canScrollNext()) api.scrollNext();
      else api.scrollTo(0);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [api, list.length]);

  if (list.length === 0) return null;

  const eyebrow = showcase?.eyebrow;
  const title = showcase?.title;

  return (
    <section className="bg-[#faf8f8] py-20 sm:py-28" data-testid="about-team-showcase">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        {(eyebrow || title) && (
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-40px" }}
            className="mx-auto max-w-3xl text-center"
          >
            {eyebrow ? (
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#3e2723]/55">{eyebrow}</p>
            ) : null}
            {title ? (
              <h2 className="mt-4 font-display text-3xl font-black tracking-tighter text-[#3e2723] sm:text-4xl lg:text-5xl">
                {title}
              </h2>
            ) : null}
          </motion.div>
        )}

        {/* Mobile: carousel */}
        <div className="mt-12 lg:hidden">
          <Carousel
            setApi={setApi}
            opts={{ loop: list.length > 1, align: "center", containScroll: "trimSnaps" }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {list.map((member, i) => (
                <CarouselItem key={`${member.name}-${i}`} className="basis-[86%] pl-4 sm:basis-[72%]">
                  <TeamMemberCard member={member} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {list.length > 1 ? (
            <div className="mt-6 flex items-center justify-center gap-2">
              {list.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Slide ${i + 1}`}
                  onClick={() => api?.scrollTo(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === activeIndex ? "w-7 bg-[#3e2723]" : "w-2 bg-[#3e2723]/25 hover:bg-[#3e2723]/40"
                  }`}
                />
              ))}
            </div>
          ) : null}
        </div>

        {/* Desktop: grid */}
        <motion.div
          variants={fadeUp}
          custom={1}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          className={`mt-12 hidden gap-5 lg:grid ${teamGridClass(list.length)}`}
        >
          {list.map((member, i) => (
            <TeamMemberCard key={`${member.name}-desktop-${i}`} member={member} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
